import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Building2, Download, Eye, HandCoins, Loader2, MapPinned, Search, Users, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";

const RUPIAH_FORMATTER = new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  maximumFractionDigits: 0,
});

const DKI_REGIONS = [
  "Jakarta Pusat",
  "Jakarta Utara",
  "Jakarta Barat",
  "Jakarta Selatan",
  "Jakarta Timur",
  "Kepulauan Seribu",
];

function formatCurrency(value) {
  if (value === null || value === undefined || value === "") {
    return "Belum dicatat";
  }
  return RUPIAH_FORMATTER.format(Number(value));
}

function formatDate(value) {
  if (!value) {
    return "Belum ditransfer";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat("id-ID", { dateStyle: "medium" }).format(date);
}

function MetricCard({ icon: Icon, label, value, tone, testId }) {
  return (
    <article className="overflow-hidden border border-gray-200 bg-white" data-testid={testId}>
      <div className="flex items-center justify-between border-b border-gray-100 bg-[#F9FAFB] px-5 py-3">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#0B6B3A]">
          Pencairan Tahap II
        </p>
        <span
          className={`flex h-8 w-8 items-center justify-center rounded-md ${tone.iconClass}`}
          aria-hidden="true"
        >
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="flex items-end justify-between gap-4 px-5 py-5">
        <div>
          <p className="text-sm font-semibold text-[#6B7280]">{label}</p>
          <p className="mt-1 font-display text-3xl font-extrabold text-[#0B6B3A]">{value}</p>
        </div>
        <span className="h-11 w-1 shrink-0 rounded-full" style={{ backgroundColor: tone.accent }} />
      </div>
    </article>
  );
}

function StageStatus({ status }) {
  const labelByStatus = {
    belum_diproses: "Belum Diproses",
    menunggu_bukti: "Menunggu Bukti",
    perlu_tinjau: "Perlu Ditinjau",
    terverifikasi: "Terverifikasi",
    disetujui: "Disetujui",
    dicairkan: "Dicairkan",
    ditunda: "Ditunda",
  };
  const isPaid = status === "dicairkan";
  return (
    <span
      className={[
        "inline-flex rounded-full px-2.5 py-1 text-xs font-bold",
        isPaid ? "bg-[#E8F6EE] text-[#0B6B3A]" : "bg-[#F3F4F6] text-[#6B7280]",
      ].join(" ")}
    >
      {labelByStatus[status] || "Belum Diproses"}
    </span>
  );
}

function PortalSortHeader({ label, field, sort, onSort }) {
  const active = sort.field === field;
  const Icon = active ? (sort.direction === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th className="px-4 py-3 font-semibold" aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => onSort(field)} data-testid={`portal-sort-${field}`} className="inline-flex w-full items-center justify-between gap-3 text-left transition-colors hover:text-[#0B6B3A]">
        {label}<Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      </button>
    </th>
  );
}

export default function StageTwoDisbursementPortal() {
  const [region, setRegion] = useState("all");
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sort, setSort] = useState({ field: "", direction: "asc" });
  const onSort = (field) => setSort((current) => ({ field, direction: current.field === field && current.direction === "asc" ? "desc" : "asc" }));

  const loadPortal = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/portal/pencairan-tahap-ii", {
        params: {
          region,
          search: query || undefined,
        },
      });
      setData(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [query, region]);

  useEffect(() => {
    const timer = window.setTimeout(loadPortal, 220);
    return () => window.clearTimeout(timer);
  }, [loadPortal]);

  const summary = data?.summary || {
    campuses: 0,
    recipients: 0,
    paid_campuses: 0,
    proofs: 0,
  };
  const displayRegion = data?.selected_region === "all"
    ? "Seluruh DKI Jakarta"
    : data?.selected_region || "Wilayah Pencairan";
  const campuses = useMemo(() => {
    const items = [...(data?.campuses || [])];
    if (!sort.field) return items;
    const getValue = (campus) => sort.field === "remarks" ? campus.stage_two.remarks.join(" · ") : campus.stage_two[sort.field];
    return items.sort((a, b) => {
      const left = getValue(a), right = getValue(b);
      if (left == null) return right == null ? 0 : 1;
      if (right == null) return -1;
      const comparison = typeof left === "number" ? left - right : String(left).localeCompare(String(right), "id", { numeric: true });
      return sort.direction === "asc" ? comparison : -comparison;
    });
  }, [data, sort]);
  const availableRegions = data?.available_regions || DKI_REGIONS;

  const openCampusDetail = async (campusKey) => {
    try {
      const response = await api.get(`/portal/pencairan-tahap-ii/campuses/${campusKey}`);
      setDetail(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError.response?.data?.detail));
    }
  };

  return (
    <main className="min-h-screen bg-[#F6FAF7] text-[#1F2937]" data-testid="stage-two-portal-page">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-[1440px] items-center px-4 py-4 sm:px-6">
          <div className="flex min-w-0 items-center gap-3" data-testid="stage-two-portal-brand">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#27AE60] font-display text-xs font-extrabold text-white shadow-md shadow-[#27AE60]/30">
              MDJ
            </span>
            <span className="min-w-0">
              <strong className="block truncate font-display text-base font-extrabold leading-tight">
                Portal Pencairan Dana
              </strong>
              <span className="block truncate text-[10px] font-semibold leading-tight text-[#6B7280]">
                Masa Depan Jakarta Scholarship
              </span>
            </span>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1440px] px-4 py-8 sm:px-6 lg:py-10">
        <section className="flex flex-col gap-4 border-l-4 border-[#27AE60] bg-[#E8F6EE] px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0B6B3A]">
              Tampilan Baca-Saja
            </p>
            <h1 className="mt-1 font-display text-2xl font-extrabold text-[#1F2937]">
              Pencairan Dana Tahap II
            </h1>
          </div>
          <div className="flex items-center gap-2 text-sm font-bold text-[#0B6B3A]" data-testid="stage-two-portal-region-label">
            <MapPinned className="h-4 w-4" />
            {displayRegion}
          </div>
        </section>

        <section className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="stage-two-portal-summary">
          <MetricCard
            icon={Building2}
            label="Kampus Terlibat"
            value={summary.campuses}
            tone={{ accent: "#27AE60", iconClass: "bg-[#E8F6EE] text-[#0B6B3A]" }}
            testId="stage-two-portal-campus-total"
          />
          <MetricCard
            icon={Users}
            label="Penerima Manfaat"
            value={summary.recipients}
            tone={{ accent: "#F2C94C", iconClass: "bg-[#FFF8D8] text-[#8A6900]" }}
            testId="stage-two-portal-recipient-total"
          />
          <MetricCard
            icon={HandCoins}
            label="Kampus Tahap II Cair"
            value={summary.paid_campuses}
            tone={{ accent: "#0B6B3A", iconClass: "bg-[#E8F6EE] text-[#0B6B3A]" }}
            testId="stage-two-portal-paid-total"
          />
          <MetricCard
            icon={Building2}
            label="Bukti Tahap II"
            value={summary.proofs}
            tone={{ accent: "#F2C94C", iconClass: "bg-[#FFF8D8] text-[#8A6900]" }}
            testId="stage-two-portal-proof-total"
          />
        </section>

        <section className="mt-7" data-testid="stage-two-portal-table-section">
          <div className="border-b-2 border-[#0B6B3A] pb-4 text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-[#1F2937]">
              Rekapitulasi Pencairan Masa Depan Jakarta Scholarship BAZNAS (BAZIS) DKI Jakarta
            </p>
            <p className="mt-1 text-xs font-bold uppercase text-[#0B6B3A]">
              Tahap II Wilayah {displayRegion}
            </p>
          </div>

          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:max-w-xl">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Cari nama kampus atau nama mahasiswa..."
                data-testid="stage-two-portal-search-input"
                className="w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-9 pr-4 text-sm outline-none focus:border-[#27AE60] focus:ring-2 focus:ring-[#27AE60]/20"
              />
            </div>
            <select
              value={region}
              onChange={(event) => setRegion(event.target.value)}
              data-testid="stage-two-portal-region-filter"
              className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm font-semibold text-[#1F2937]"
            >
              <option value="all">Keseluruhan Wilayah</option>
              {availableRegions.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </select>
          </div>

          {error ? (
            <div className="mt-5 border border-[#FECACA] bg-white px-5 py-8 text-center">
              <p className="text-sm font-bold text-[#991B1B]" data-testid="stage-two-portal-error">
                {error}
              </p>
            </div>
          ) : (
            <div className="mt-5 overflow-x-auto border border-gray-200 bg-white">
              <table className="w-full min-w-[1080px] text-left text-sm" data-testid="stage-two-portal-table">
                <thead className="border-b border-gray-100 bg-[#F9FAFB] text-xs text-[#6B7280]">
                  <tr>
                    <th className="px-4 py-3 font-semibold">No.</th>
                    <th className="px-4 py-3 font-semibold">Nama Perguruan Tinggi</th>
                    <th className="px-4 py-3 text-center font-semibold">Jumlah Mahasiswa</th>
                    <th className="px-4 py-3 font-semibold">Nama Mahasiswa</th>
                    <th className="px-4 py-3 font-semibold">Wilayah</th>
                    <PortalSortHeader label="Nominal Pengajuan" field="expected_amount" sort={sort} onSort={onSort} />
                    <PortalSortHeader label="Waktu Transfer" field="disbursed_at" sort={sort} onSort={onSort} />
                    <PortalSortHeader label="Nominal Transfer" field="transfer_amount" sort={sort} onSort={onSort} />
                    <PortalSortHeader label="Bukti TF" field="proof_count" sort={sort} onSort={onSort} />
                    <PortalSortHeader label="Keterangan" field="remarks" sort={sort} onSort={onSort} />
                    <th className="px-4 py-3 text-right font-semibold">Rincian</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={11} className="px-5 py-12 text-center" data-testid="stage-two-portal-loading">
                        <Loader2 className="mx-auto h-5 w-5 animate-spin text-[#27AE60]" />
                      </td>
                    </tr>
                  ) : campuses.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-5 py-12 text-center text-sm text-[#6B7280]" data-testid="stage-two-portal-empty">
                        Belum ada data Pencairan Tahap II pada pencarian atau wilayah ini.
                      </td>
                    </tr>
                  ) : campuses.map((campus, index) => (
                    <tr key={campus.campus_key} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-4 font-semibold text-[#6B7280]">{index + 1}</td>
                      <td className="px-4 py-4 font-bold text-[#1F2937]">{campus.campus}</td>
                      <td className="px-4 py-4 text-center font-bold text-[#1F2937]">
                        {campus.recipient_count}
                      </td>
                      <td className="px-4 py-4">
                        <ol className="space-y-1 text-xs text-[#1F2937]" data-testid={`stage-two-portal-students-${campus.campus_key}`}>
                          {campus.students.map((student, studentIndex) => (
                            <li key={`${student.name}-${studentIndex}`}>{studentIndex + 1}. {student.name}</li>
                          ))}
                        </ol>
                      </td>
                      <td className="px-4 py-4 text-xs text-[#6B7280]">{campus.regions.join(", ")}</td>
                      <td className="px-4 py-4 font-bold tabular-nums text-[#1F2937]" data-testid={`portal-requested-amount-${campus.campus_key}`}>
                        {formatCurrency(campus.stage_two.expected_amount)}
                      </td>
                      <td className="px-4 py-4 text-xs text-[#6B7280]" data-testid={`portal-transfer-date-${campus.campus_key}`}>
                        {formatDate(campus.stage_two.disbursed_at)}
                      </td>
                      <td className="px-4 py-4" data-testid={`portal-transfer-amount-${campus.campus_key}`}>
                        {campus.stage_two.transfer_amount ? (
                          <span className="font-bold tabular-nums text-[#1F2937]">{formatCurrency(campus.stage_two.transfer_amount)}</span>
                        ) : <span className="text-xs text-[#6B7280]">Belum ada transfer</span>}
                      </td>
                      <td className="px-4 py-4" data-testid={`portal-proofs-${campus.campus_key}`}>
                        {campus.stage_two.proofs?.length ? (
                          <ul className="space-y-2">
                            {campus.stage_two.proofs.map((proof) => <PortalProofDownload key={`${proof.region}-${proof.source_id}`} campusKey={campus.campus_key} region={proof.region} proof={proof} compact />)}
                          </ul>
                        ) : <span className="text-xs text-[#6B7280]">Belum ada bukti</span>}
                      </td>
                      <td className="px-4 py-4 text-xs text-[#6B7280]">
                        {campus.stage_two.remarks.length ? campus.stage_two.remarks.join(" · ") : "—"}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => openCampusDetail(campus.campus_key)}
                          data-testid={`open-stage-two-portal-detail-${campus.campus_key}`}
                          className="rounded-lg bg-[#E8F6EE] px-3 py-2 text-xs font-bold text-[#0B6B3A] transition-colors hover:bg-[#D3EFDF]"
                        >
                          Buka Rincian
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
      {detail && (
        <StageTwoPortalDetail
          detail={detail}
          onClose={() => setDetail(null)}
        />
      )}
    </main>
  );
}

function PortalProofDownload({ campusKey, region, proof, compact = false }) {
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const testId = `${compact ? "table" : "detail"}-${campusKey}-${region}-${proof.source_id}`;
  useEffect(() => () => { if (preview) window.URL.revokeObjectURL(preview.url); }, [preview]);
  const openProof = async (action) => {
    setBusy(action);
    setError("");
    try {
      const response = await api.get(
        `/portal/pencairan-tahap-ii/campuses/${encodeURIComponent(campusKey)}/regions/${encodeURIComponent(region)}/proofs/${encodeURIComponent(proof.source_id)}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(response.data);
      if (action === "preview") {
        setPreview({ url, type: response.data.type });
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = proof.filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      }
    } catch (requestError) {
      let message = "Bukti transfer tidak dapat dibuka. Silakan coba lagi.";
      if (requestError.response?.data instanceof Blob) {
        try { message = formatApiError(JSON.parse(await requestError.response.data.text()).detail); }
        catch { /* Respons bukan JSON: gunakan pesan umum. */ }
      }
      setError(message);
    } finally {
      setBusy("");
    }
  };
  return (
    <li className={compact ? "min-w-40 space-y-1" : "rounded-lg border border-gray-200 bg-white p-3"} data-testid={`public-stage-two-proof-${testId}`}>
      <div className={compact ? "space-y-1" : "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"}>
        <button type="button" onClick={() => openProof("preview")} disabled={!!busy}
          data-testid={`preview-stage-two-proof-${testId}`} title={proof.filename}
          className="inline-flex min-w-0 items-center gap-2 text-left text-xs font-semibold text-[#0B6B3A] transition-colors hover:text-[#27AE60] hover:underline disabled:opacity-60">
          {busy === "preview" ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Eye className="h-4 w-4 shrink-0" />}
          <span className="break-all" data-testid={`public-stage-two-proof-name-${testId}`}>{proof.filename}</span>
        </button>
        <button type="button" onClick={() => openProof("download")} disabled={!!busy} aria-busy={busy === "download"}
          data-testid={`download-stage-two-proof-${testId}`}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold text-[#0B6B3A] transition-colors hover:bg-[#E8F6EE] disabled:opacity-60">
          {busy === "download" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          {busy === "download" ? "Mengunduh..." : compact ? "Unduh" : "Unduh Bukti Transfer"}
        </button>
      </div>
      {error && <p role="alert" className="mt-2 text-xs text-[#991B1B]" data-testid={`download-stage-two-proof-error-${testId}`}>{error}</p>}
      <Dialog open={!!preview} onOpenChange={(open) => { if (!open) setPreview(null); }}>
        <DialogContent className="z-[60] max-h-[92vh] w-[calc(100%_-_2rem)] max-w-4xl overflow-y-auto" data-testid={`proof-preview-modal-${testId}`}>
          <DialogTitle className="break-all pr-6 text-base" data-testid={`proof-preview-title-${testId}`}>{proof.filename}</DialogTitle>
          <DialogDescription>Bukti Transfer Tahap II · {region}</DialogDescription>
          {preview && (preview.type.startsWith("image/") ? (
            <img src={preview.url} alt={`Bukti transfer ${proof.filename}`} className="max-h-[60vh] w-full object-contain" data-testid={`proof-preview-image-${testId}`} />
          ) : (
            <iframe src={preview.url} title={`Pratinjau ${proof.filename}`} className="h-[60vh] w-full border border-gray-200" data-testid={`proof-preview-document-${testId}`} />
          ))}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => setPreview(null)} data-testid={`close-proof-preview-${testId}`} className="rounded-lg border px-4 py-2 text-sm transition-colors hover:bg-gray-50">Tutup</button>
            <a href={preview?.url} download={proof.filename} data-testid={`download-proof-preview-${testId}`} className="inline-flex items-center gap-2 rounded-lg bg-[#0B6B3A] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#07532D]"><Download className="h-4 w-4" />Unduh Bukti</a>
          </div>
        </DialogContent>
      </Dialog>
    </li>
  );
}

function StageTwoPortalDetail({ detail, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="stage-two-portal-detail-modal"
    >
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between border-b border-gray-100 bg-white px-6 py-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-[#0B6B3A]">
              Pencairan Dana Tahap II
            </p>
            <h2 className="mt-1 font-display text-xl font-extrabold text-[#1F2937]">
              {detail.campus}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="close-stage-two-portal-detail"
            className="rounded-lg p-2 text-[#6B7280] transition-colors hover:bg-gray-100"
            aria-label="Tutup rincian pencairan tahap dua"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4 p-6">
          {detail.regions.map((item) => (
            <section key={item.region} className="border border-gray-200 bg-[#F9FAFB] p-4">
              <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
                <div>
                  <h3 className="font-display text-lg font-bold text-[#1F2937]">{item.region}</h3>
                  <p className="mt-1 text-sm text-[#6B7280]">
                    {item.recipient_count} mahasiswa penerima manfaat
                  </p>
                </div>
                <StageStatus status={item.stage_two.status} />
              </div>
              <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-xs text-[#6B7280]">Nominal Pengajuan</dt>
                  <dd className="mt-1 font-bold text-[#1F2937]">
                    {formatCurrency(item.stage_two.expected_amount)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-[#6B7280]">Nominal Transfer</dt>
                  <dd className="mt-1 font-bold text-[#1F2937]">
                    {item.stage_two.transfer_amount ? formatCurrency(item.stage_two.transfer_amount) : <span className="font-normal text-[#6B7280]">Belum ada transfer</span>}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-[#6B7280]">Waktu Transfer</dt>
                  <dd className="mt-1 font-bold text-[#1F2937]">
                    {formatDate(item.stage_two.disbursed_at)}
                  </dd>
                </div>
              </dl>
              <div className="mt-4 border-t border-gray-200 pt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-[#0B6B3A]">
                  Bukti Transfer
                </p>
                {item.stage_two.proof_count > 0 && <p className="mt-2 text-sm text-[#6B7280]" data-testid={`public-stage-two-proof-count-${item.region}`}>
                  Bukti TF tercatat: {item.stage_two.proof_count}
                </p>}
                {item.stage_two.proofs?.length ? (
                  <ul className="mt-3 space-y-2" data-testid={`public-stage-two-proof-list-${item.region}`}>
                    {item.stage_two.proofs.map((proof) => (
                      <PortalProofDownload key={proof.source_id} campusKey={detail.campus_key} region={item.region} proof={proof} />
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-sm text-[#6B7280]" data-testid={`public-stage-two-proof-empty-${item.region}`}>
                    Belum ada bukti
                  </p>
                )}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}